// src/features/editor/lessonsApi.ts
import { api } from '@/store/api'
import type { Lesson, LessonCreate, LessonUpdate } from '@/types/lesson'
import type { Block } from '@/types/blocks'
import { coursesApi } from './coursesApi'
import { normalizeArticleBlock } from './lib/articleBlock'
import { runOptimistic } from './lib/optimisticUpdate'
import { applyOrderUpdates, sortByOrder } from './lib/orderUtils'
import { runSequentialPuts } from './lib/sequentialPuts'

export const lessonsApi = api.injectEndpoints({
  endpoints: (build) => ({
    createLesson: build.mutation<Lesson, { courseId: number; body: LessonCreate }>({
      query: ({ courseId, body }) => ({
        url: `gamified-courses/${courseId}/lessons`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ courseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: newLesson } = await queryFulfilled
          dispatch(
            coursesApi.util.updateQueryData('getEditorCourse', courseId, (draft) => {
              const { blocks: _blocks, ...lessonWithoutBlocks } = newLesson
              draft.lessons.push(lessonWithoutBlocks)
            }),
          )
        } catch {
          // mutation failed; nothing to update
        }
      },
      invalidatesTags: (_r, _e, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
    updateLesson: build.mutation<Lesson, { id: number; patch: LessonUpdate; courseId: number }>({
      query: ({ id, patch }) => ({
        url: `gamified-courses/lessons/${id}`,
        method: 'PUT',
        body: patch,
      }),
      async onQueryStarted({ id, courseId, patch }, { dispatch, queryFulfilled }) {
        const cachePatch = dispatch(
          coursesApi.util.updateQueryData('getEditorCourse', courseId, (draft) => {
            const lesson = draft.lessons.find((l) => l.id === id)
            if (!lesson) return
            Object.assign(lesson, patch)
          }),
        )
        await runOptimistic(queryFulfilled, cachePatch)
      },
      invalidatesTags: (_r, _e, { id, courseId }) => [
        { type: 'Lesson', id },
        { type: 'Course', id: courseId },
      ],
    }),
    deleteLesson: build.mutation<void, { id: number; courseId: number }>({
      // `404` = урока уже нет — удаление удалось, тостов не нужно (как у экрана).
      queryFn: async ({ id }, _api, _extra, baseQuery) => {
        const res = await baseQuery({ url: `gamified-courses/lessons/${id}`, method: 'DELETE' })
        if (res.error && res.error.status !== 404) return { error: res.error }
        return { data: undefined }
      },
      async onQueryStarted({ id, courseId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          coursesApi.util.updateQueryData('getEditorCourse', courseId, (draft) => {
            draft.lessons = draft.lessons.filter((l) => l.id !== id)
          }),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      // Курс не перечитываем: урок уже убран из кеша курса (выше), сервер ответит тем же,
      // а перечитывание при быстрых удалениях подряд возвращало в ленту удаляемый урок.
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Lesson', id }],
    }),
    getLessonBlocks: build.query<Block[], number>({
      query: (lessonId) => `gamified-courses/lessons/${lessonId}/blocks`,
      transformResponse: (blocks: Block[]) => sortByOrder(blocks).map(normalizeArticleBlock),
      providesTags: (_r, _e, lessonId) => [{ type: 'Block', id: `lesson-${lessonId}` }],
    }),
    // Persists a new lesson order. Full-body PUTs via runSequentialPuts —
    // partial bodies reset missing fields on this backend.
    reorderLessons: build.mutation<
      void,
      { courseId: number; updates: Array<{ id: number; order: number; patch: LessonUpdate }> }
    >({
      queryFn: ({ updates }, _api, _extra, baseQuery) =>
        runSequentialPuts(
          baseQuery,
          updates.map((u) => ({ url: `gamified-courses/lessons/${u.id}`, body: u.patch })),
        ),
      async onQueryStarted({ courseId, updates }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          coursesApi.util.updateQueryData('getEditorCourse', courseId, (draft) => {
            applyOrderUpdates(draft.lessons, updates)
          }),
        )
        await runOptimistic(queryFulfilled, patch)
      },
      invalidatesTags: (_r, _e, { courseId }) => [{ type: 'Course', id: courseId }],
    }),
  }),
})

export const {
  useCreateLessonMutation,
  useUpdateLessonMutation,
  useDeleteLessonMutation,
  useGetLessonBlocksQuery,
  useReorderLessonsMutation,
} = lessonsApi
